import { capitalCase } from "change-case";
import { useEffect, useState } from "react";

import { useToast } from "@smart/bridge-intake-components-dom";
import {
  SubmissionLinkHashNS,
  extractPrefixedTeamId,
  Country,
} from "@smart/bridge-types-basic";
import { loadLocale } from "@smart/itops-locale-dom";
import { Button, Link } from "@smart/itops-sb-design-system-dom";
import { useSmokeballApp } from "@smart/itops-smokeball-app-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import { extractId } from "@smart/itops-types-basic";
import { toSubdomain } from "@smart/itops-utils-basic";
import {
  useUpdateSubmissionLinkHash,
  useLoadTeamForms,
  useLoadGlobalForms,
  useLoadTeam,
  useLazyLoadFields,
  useLazyLoadMatterTypes,
  useLoadSubmissionLinkHashes,
  useDeleteSubmissionLinkHash,
  useLoadSetting,
} from "@smart/manage-gql-client-dom";
import { useUser } from "@smart/manage-hooks-dom";

import { ChangeSettings } from "./change-settings";
import { CreateLink } from "./create";
import { DeleteLinkModal } from "./delete";
import { LinksTable } from "./links-table";
import { SubmissionLink } from "./types";
import { GenerateQRCode } from "../../shared";

const GenerateHTML = () => {
  const { user } = useUser();
  const { staff } = useSmokeballApp();
  const { setToasts } = useToast();
  const [updateSubmissionLinkHash, submissionLinkHash] =
    useUpdateSubmissionLinkHash();
  const team = useLoadTeam({});
  const links = useLoadSubmissionLinkHashes({});
  const { result: settings } = useLoadSetting({});
  const teamForms = useLoadTeamForms({});
  const globalForms = useLoadGlobalForms({});
  const loadFields = useLazyLoadFields();
  const [loadMatterTypes] = useLazyLoadMatterTypes();
  const generateLinkModal = useState(false);
  const changeStaffModal = useState(false);
  const [editingLink, setEditingLink] = useState<SubmissionLink>();
  const [deletingLink, setDeletingLink] = useState<SubmissionLink>();
  const [deleteLink, deleteLinkResult] = useDeleteSubmissionLinkHash();
  const [qrCodeLink, setQrCodeLink] = useState<string>();
  const providerInfo = useProviderInfo();
  const productName = providerInfo?.label || "Smokeball";
  const locale = loadLocale();

  const teamFormResult =
    teamForms.result?.filter(
      ({ values }) =>
        !values.deleted && values.active && values.category === "lead",
    ) || [];
  const globalFormResult =
    globalForms.result?.filter(
      ({ values }) =>
        !values.deleted && values.active && values.category === "lead",
    ) || [];
  const staffResult = staff?.current?.filter((s) => s.enabled) || [];

  useEffect(() => {
    generateLinkModal[1](false);
    changeStaffModal[1](!!editingLink);
  }, [editingLink]);

  const learnMoreUrl: Record<Country, string> = {
    AU: "https://support.smokeball.com/hc/en-au/articles/8015208933911",
    GB: "https://support.smokeball.com/hc/en-gb/articles/8015208933911",
    US: "https://support.smokeball.com/hc/en-us/articles/8015208933911",
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex justify-between items-center p-[1.6rem] flex-shrink-0">
        <div className="flex flex-col gap-xSmall">
          <p
            data-testid="embed-label"
            className="text-ui"
          >{`Embed your Intake Form in your website so Web ${capitalCase(locale.terms.enquiry.plural)} flow directly into ${productName}`}</p>
          <Link
            text="Learn More"
            target="_blank"
            href={learnMoreUrl[locale.country]}
          />
        </div>
        <Button
          text="Create Link"
          onClick={() => generateLinkModal[1](true)}
          variant="primarySubtle"
        />
      </div>
      <LinksTable
        loading={
          links.loading ||
          staff?.loading ||
          teamForms.loading ||
          globalForms.loading
        }
        links={
          links.data?.submissionLinkHashes.filter(
            (l) => !l.deleted,
          ) as SubmissionLink[]
        }
        forms={[...teamFormResult, ...globalFormResult]}
        staff={staffResult}
        user={
          user && {
            ...user,
            userId: extractPrefixedTeamId(user.userUri, "sb").id,
          }
        }
        onEdit={setEditingLink}
        onDelete={setDeletingLink}
        onCopy={async ({ code, toast }) => {
          await navigator.clipboard.writeText(code);
          setToasts([toast]);
        }}
        onGenerateQRCode={setQrCodeLink}
      />
      <div className="flex-shrink-0">
        <CreateLink
          creatingModal={generateLinkModal}
          loading={
            staff?.loading ||
            teamForms.loading ||
            globalForms.loading ||
            submissionLinkHash.loading
          }
          areasOfLaw={settings?.areasOfLaw?.map((area) => area.trim()) || []}
          locations={settings?.locations || []}
          teamName={team.result?.values.name || "Team"}
          teamForms={teamFormResult}
          globalForms={settings?.disableGlobalForms ? [] : globalFormResult}
          productName={productName}
          staff={staffResult}
          user={
            user && {
              ...user,
              userId: extractPrefixedTeamId(user.userUri, "sb").id,
            }
          }
          onGenerate={async (fields) => {
            const link = await updateSubmissionLinkHash({
              variables: {
                uri: SubmissionLinkHashNS.generateUri(),
                fields,
              },
            });
            if (!link.data) return undefined;

            return toSubdomain(
              undefined,
              `/create/${extractId(link.data.generateLinkParameter.uri)}`,
            );
          }}
          onCopy={async ({ code, toast }) => {
            await navigator.clipboard.writeText(code);
            setToasts([toast]);
          }}
          loadFields={loadFields}
          loadMatterTypes={loadMatterTypes}
        />
      </div>
      <ChangeSettings
        editModal={changeStaffModal}
        editingLink={editingLink}
        form={[...teamFormResult, ...globalFormResult].find(
          (f) => f.uri === editingLink?.formUri,
        )}
        staff={staffResult}
        user={
          user && {
            ...user,
            userId: extractPrefixedTeamId(user.userUri, "sb").id,
          }
        }
        onSave={async (fields) => {
          if (!editingLink) return;

          await updateSubmissionLinkHash({
            variables: {
              uri: editingLink.uri,
              fields,
            },
          });
        }}
        onDone={() => setEditingLink(undefined)}
      />
      <DeleteLinkModal
        deleteModal={[!!deletingLink, () => {}]}
        loading={deleteLinkResult.loading}
        onReset={() => setDeletingLink(undefined)}
        update={async () => {
          if (!deletingLink) return;

          try {
            await deleteLink({ variables: { uri: deletingLink.uri } });
            setDeletingLink(undefined);
            setToasts([{ text: "Link successfully deleted" }]);
          } catch (error) {
            setToasts([
              {
                text: `Failed to delete web ${locale.terms.enquiry.name} link`,
                palette: "destructive",
              },
            ]);
            setDeletingLink(undefined);
            console.error(error);
          }
        }}
      />
      <GenerateQRCode
        link={qrCodeLink}
        onClose={() => setQrCodeLink(undefined)}
        onCopied={() => setToasts([{ text: "QR code copied!" }])}
      />
    </div>
  );
};

export { GenerateHTML };
